import React, { useCallback, useEffect, useMemo, useRef } from "react";

// Routing
import { useHistory } from "react-router-dom";

// Utils
import moment from "moment-timezone";

// Redux
import { useSelector } from "react-redux";

// Components
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import { AgGridReact } from "ag-grid-react";

// Icons
import VisibilityIcon from "@material-ui/icons/Visibility";

const OrdersTableComponent = ({ ordersArray, isLoading }) => {
  // Routing
  const history = useHistory();

  // Redux
  const { mode } = useSelector((state) => state.ui);

  const rowData = useMemo(() => {
    // Need to spread array so ag-grid doesn't mutate the frozen redux state
    return (ordersArray || []).map((order) => ({
      ...order,
    }));
  }, [ordersArray]);

  const rowCount = rowData.length;

  const currencyFormatter = useMemo(() => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    });
  }, []);

  const ActionCellRenderer = useCallback(
    (params) => {
      const { value } = params;

      return (
        <a
          href={`/sales/orders/${value}`}
          onClick={(e) => {
            if (!e.ctrlKey && !e.metaKey && !e.shiftKey && e.button === 0) {
              e.preventDefault();
              history.push(`/sales/orders/${value}`);
            }
          }}
          style={{ textDecoration: "none" }}
        >
          <Button
            size="small"
            component="span"
            startIcon={<VisibilityIcon />}
            color="primary"
          >
            View
          </Button>
        </a>
      );
    },
    [history]
  );

  const columnDefs = useMemo(() => {
    return [
      {
        headerName: "Order ID",
        field: "order_id",
        filter: "agNumberColumnFilter",
        pinned: "left",
        width: 120,
        sort: "desc",
      },
      {
        headerName: "Type",
        field: "type",
        filter: "agTextColumnFilter",
        valueFormatter: (params) => {
          if (params.value === "condo") {
            return "CONDO";
          }
          if (params.value === "package") {
            return "PACKAGE";
          }
          return params.value;
        },
      },
      {
        headerName: "Date Placed",
        field: "date_order_placed",
        minWidth: 180,
        valueFormatter: (params) => {
          if (!params.value) {
            return "";
          }

          return moment(params.value).tz("America/Chicago").format("lll");
        },
        cellStyle: {
          whiteSpace: "nowrap",
        },
      },
      {
        headerName: "Check In",
        field: "check_in",
        minWidth: 140,
        valueFormatter: (params) => {
          return params.value ? moment(params.value).utc().format("ll") : "";
        },
        cellStyle: {
          whiteSpace: "nowrap",
        },
      },
      {
        headerName: "Check Out",
        field: "check_out",
        minWidth: 140,
        valueFormatter: (params) => {
          return params.value ? moment(params.value).utc().format("ll") : "";
        },
        cellStyle: {
          whiteSpace: "nowrap",
        },
      },
      {
        headerName: "Package",
        field: "package_name",
      },
      {
        headerName: "Hotel",
        field: "hotel_name",
        cellStyle: (params) => {
          const { value } = params;

          if (!value) {
            return undefined;
          }

          // Generate consistent hex color based on text value
          const getColorFromText = (text) => {
            switch (text) {
              case "La Copa Inn Beach Hotel":
                return "#1c9ded";
              case "Sand Rose Beach Resort":
                return "#a55bcd";
              case "Courtyard by Marriott":
                return "#c49332";
              case "Hilton Garden Inn":
                return "#21ac80";
              default:
                return "#536263";
            }
          };

          return {
            color: "white",
            backgroundColor: getColorFromText(value),
            fontWeight: "bold",
          };
        },
      },
      {
        headerName: "Room",
        field: "room_name",
      },
      {
        headerName: "Subtotal",
        field: "subtotal",
        type: "rightAligned",
        valueFormatter: (params) => {
          return params.value
            ? currencyFormatter.format(Number(params.value))
            : "";
        },
      },
      {
        headerName: "Tax",
        field: "tax",
        type: "rightAligned",
        valueFormatter: (params) => {
          return params.value
            ? currencyFormatter.format(Number(params.value))
            : "";
        },
      },
      {
        headerName: "Total",
        field: "total",
        type: "rightAligned",
        valueFormatter: (params) => {
          return params.value
            ? currencyFormatter.format(Number(params.value))
            : "";
        },
      },
      {
        headerName: "Status",
        field: "order_status",
        filter: "agTextColumnFilter",
        cellStyle: (params) => {
          if (params.value === "PENDING") {
            return {
              color: "white",
              backgroundColor: "#dfad1dbb",
              fontWeight: "bold",
            };
          }

          if (params.value === "COMPLETE") {
            return {
              color: "white",
              backgroundColor: "#19a86dbb",
              fontWeight: "bold",
            };
          }

          if (params.value === "CANCELLED") {
            return {
              color: "white",
              backgroundColor: "#de4141bb",
              fontWeight: "bold",
            };
          }

          return undefined;
        },
      },
      {
        headerName: "Reservation Status",
        field: "reservation_status",
        filter: "agTextColumnFilter",
        cellStyle: (params) => {
          if (params.value === "PENDING") {
            return {
              color: "white",
              backgroundColor: "#dfad1dbb",
              fontWeight: "bold",
            };
          }

          if (params.value === "RESERVED") {
            return {
              color: "white",
              backgroundColor: "#19a86dbb",
              fontWeight: "bold",
            };
          }

          if (params.value === "CANCELLED") {
            return {
              color: "white",
              backgroundColor: "#de4141bb",
              fontWeight: "bold",
            };
          }

          return undefined;
        },
      },
      {
        headerName: "Actions",
        field: "order_id",
        colId: "actions",
        pinned: "right",
        width: 140,
        cellRendererFramework: ActionCellRenderer,
        sortable: false,
        filter: false,
        resizable: false,
      },
    ];
  }, [ActionCellRenderer, currencyFormatter]);

  const defaultColDef = useMemo(() => {
    return {
      sortable: true,
      filter: true,
      resizable: true,
      flex: 1,
      minWidth: 120,
    };
  }, []);

  const gridApiRef = useRef(null);

  const onGridReady = useCallback(
    (params) => {
      gridApiRef.current = params.api;

      if (isLoading) {
        params.api.showLoadingOverlay();
      } else if (!rowCount) {
        params.api.showNoRowsOverlay();
      }
    },
    [isLoading, rowCount]
  );

  useEffect(() => {
    const api = gridApiRef.current;

    if (!api) {
      return;
    }

    if (isLoading) {
      api.showLoadingOverlay();
      return;
    }

    if (rowCount === 0) {
      api.showNoRowsOverlay();
      return;
    }
    api.hideOverlay();
  }, [isLoading, rowCount]);

  return (
    <Grid container item xs={12}>
      <Grid item xs={12}>
        <Typography component="h2" variant="h6" gutterBottom>
          Customer Orders
        </Typography>
        <div
          className={mode === "dark" ? "ag-theme-alpine-dark" : "ag-theme-alpine"}
          style={{ height: 400, width: "100%" }}
        >
          <AgGridReact
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            enableCellTextSelection
            animateRows
            overlayLoadingTemplate="<span class='ag-overlay-loading-center'>Loading orders...</span>"
            overlayNoRowsTemplate="<span class='ag-overlay-no-rows-center'>No orders to display</span>"
            onGridReady={onGridReady}
          />
        </div>
      </Grid>
    </Grid>
  );
};

export default OrdersTableComponent;
